import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useManagedUsers } from '../UsersContext';
import { useLanguage } from '../i18n/LanguageContext';
import { normalizeRole, roleLabelEn } from '../utils/userLocale';
import { apiJson } from '../apiClient';
import './UsersPage.css';

function IconEye() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" strokeLinecap="round" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconPencil() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm18.71-9.21a.996.996 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" strokeLinecap="round" />
    </svg>
  );
}

function IconMore() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

function IconInfo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
    </svg>
  );
}

function roleBadgeClass(role) {
  const r = normalizeRole(role);
  if (r === 'owner') return 'users-badge users-badge-owner';
  if (r === 'manager') return 'users-badge users-badge-manager';
  return 'users-badge users-badge-staff';
}

function UserActionsCell({ row, currentUserId, removingId, onView, onEdit, onRemove, onNavigate, menuOpenId, setMenuOpenId }) {
  const isSelf = currentUserId != null && row.id === currentUserId;
  const canEdit = !isSelf;
  const canDelete = !isSelf && normalizeRole(row.role) !== 'owner';

  const selfHint = 'Use profile menu to change your own account';

  return (
    <div className="users-actions-wrap">
      <button
        type="button"
        className="users-act-btn users-act-btn--view"
        title="View details"
        aria-label="View details"
        onClick={() => onView(row)}
      >
        <IconEye />
      </button>
      {canEdit && (
        <>
          <button type="button" className="users-act-btn users-act-btn--edit" title="Edit user" aria-label="Edit user" onClick={() => onEdit(row)}>
            <IconPencil />
          </button>
          <div className="users-more-root" id={`users-more-root-${row.id}`}>
            <button
              type="button"
              className={`users-act-btn users-act-btn--more${menuOpenId === row.id ? ' is-open' : ''}`}
              title="More actions"
              aria-label="More actions"
              aria-expanded={menuOpenId === row.id}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpenId((v) => (v === row.id ? null : row.id));
              }}
            >
              <IconMore />
            </button>
            {menuOpenId === row.id && (
              <ul className="users-more-menu" role="menu">
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    className="users-more-item"
                    onClick={() => {
                      void navigator.clipboard?.writeText(row.email).catch(() => {});
                      setMenuOpenId(null);
                    }}
                  >
                    Copy email
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    className="users-more-item"
                    onClick={() => {
                      setMenuOpenId(null);
                      onNavigate('users-add');
                    }}
                  >
                    Add another user…
                  </button>
                </li>
              </ul>
            )}
          </div>
        </>
      )}
      {canDelete && (
        <button
          type="button"
          className="users-act-btn users-act-btn--del"
          title="Remove user"
          aria-label="Remove user"
          disabled={removingId === row.id}
          onClick={() => void onRemove(row)}
        >
          {removingId === row.id ? <span className="users-act-spinner" aria-hidden /> : <IconTrash />}
        </button>
      )}
      {isSelf && (
        <span className="users-act-btn users-act-btn--hint users-act-hint-static" title={selfHint} role="note" aria-label={selfHint}>
          <IconInfo />
        </span>
      )}
    </div>
  );
}

const UsersListPage = ({ onNavigate, user }) => {
  const { t } = useLanguage();
  const userRole = normalizeRole(user?.role);
  const isOwner = userRole === 'owner';
  const isManager = userRole === 'manager';
  const branchName = user?.branch?.name || t('overview.selectedBranch');
  const { users, usersLoading, removeUser, refreshUsers, updateUser } = useManagedUsers();
  const [removingId, setRemovingId] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editPassword2, setEditPassword2] = useState('');
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [editRole, setEditRole] = useState('employee');
  const [editBranchId, setEditBranchId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [resetRequests, setResetRequests] = useState([]);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetActionId, setResetActionId] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPassword2, setResetPassword2] = useState('');
  const [resetNote, setResetNote] = useState('');

  const filteredUsers = useMemo(() => {
    if (!users?.length) return [];
    let list = [...users];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (u) =>
          (u.name || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          (u.phone || '').toLowerCase().includes(q) ||
          String(u.id ?? '').includes(q)
      );
    }
    if (roleFilter !== 'all') {
      list = list.filter((u) => normalizeRole(u.role) === roleFilter);
    }
    return list;
  }, [users, searchQuery, roleFilter]);

  useEffect(() => {
    if (!isOwner) return undefined;
    let mounted = true;
    (async () => {
      try {
        const data = await apiJson('/owner/branches');
        if (!mounted) return;
        setBranches(Array.isArray(data.branches) ? data.branches : []);
      } catch {
        if (!mounted) return;
        setBranches([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isOwner]);

  useEffect(() => {
    if (!editUser) return;
    setEditName(editUser.name || '');
    setEditEmail(editUser.email || '');
    setEditPhone(editUser.phone || '');
    setEditPassword('');
    setEditPassword2('');
    setEditError('');
    const r = normalizeRole(editUser.role);
    setEditRole(r === 'owner' || r === 'manager' || r === 'employee' ? r : 'employee');
    setEditBranchId(editUser.branchId != null && editUser.branchId !== '' ? String(editUser.branchId) : '');
  }, [editUser]);

  useEffect(() => {
    if (menuOpenId == null) return undefined;
    const fn = (e) => {
      const el = document.getElementById(`users-more-root-${menuOpenId}`);
      if (el && !el.contains(e.target)) setMenuOpenId(null);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [menuOpenId]);

  const loadResetRequests = useCallback(async () => {
    if (normalizeRole(user?.role) !== 'owner') return;
    setResetLoading(true);
    setResetError('');
    try {
      const data = await apiJson('/owner/password-reset-requests');
      setResetRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      setResetError(err?.message || 'Could not load password reset requests.');
      setResetRequests([]);
    } finally {
      setResetLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    void loadResetRequests();
  }, [loadResetRequests]);

  const pendingResetRequests = useMemo(
    () => resetRequests.filter((r) => ['pending', 'code_sent', 'verified'].includes(String(r.status))),
    [resetRequests]
  );

  const onRemove = useCallback(
    async (row) => {
      if (user?.id != null && row.id === user.id) return;
      const ok = window.confirm(`Remove ${row.name} (${row.email})? They will no longer be able to sign in.`);
      if (!ok) return;
      setRemovingId(row.id);
      try {
        await removeUser(row.id);
      } finally {
        setRemovingId(null);
      }
    },
    [removeUser, user?.id]
  );

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    setEditError('');
    if (editPassword && editPassword.length < 8) {
      setEditError('New password must be at least 8 characters (or leave both password fields empty).');
      return;
    }
    if (editPassword !== editPassword2) {
      setEditError('Passwords do not match.');
      return;
    }
    setEditSaving(true);
    const res = await updateUser(editUser.id, {
      name: editName.trim(),
      email: editEmail.trim().toLowerCase() || undefined,
      phone: editPhone.trim() || undefined,
      role: editRole,
      password: editPassword || undefined,
      branchId: editBranchId ? Number(editBranchId) : undefined,
    });
    setEditSaving(false);
    if (!res.ok) {
      setEditError(res.error || 'Update failed.');
      return;
    }
    setEditUser(null);
  };

  const openResetModal = useCallback((req) => {
    setResetTarget(req);
    setResetPassword('');
    setResetPassword2('');
    setResetNote(req.note || '');
    setResetError('');
  }, []);

  const dismissResetRequest = useCallback(async (req) => {
    if (!window.confirm(`Dismiss password reset request for ${req.email}?`)) return;
    setResetActionId(req.id);
    setResetError('');
    try {
      await apiJson(`/owner/password-reset-requests/${req.id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ action: 'dismiss', note: req.note || undefined }),
      });
      await loadResetRequests();
    } catch (err) {
      setResetError(err?.message || 'Could not dismiss request.');
    } finally {
      setResetActionId(null);
    }
  }, [loadResetRequests]);

  const submitPasswordReset = useCallback(async (e) => {
    e.preventDefault();
    if (!resetTarget) return;
    setResetError('');
    if (String(resetPassword).length < 8) {
      setResetError('New password must be at least 8 characters.');
      return;
    }
    if (resetPassword !== resetPassword2) {
      setResetError('Passwords do not match.');
      return;
    }
    setResetActionId(resetTarget.id);
    try {
      await apiJson(`/owner/password-reset-requests/${resetTarget.id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'reset',
          newPassword: resetPassword,
          note: resetNote.trim() || undefined,
        }),
      });
      setResetTarget(null);
      setResetPassword('');
      setResetPassword2('');
      await loadResetRequests();
    } catch (err) {
      setResetError(err?.message || 'Could not reset password.');
    } finally {
      setResetActionId(null);
    }
  }, [loadResetRequests, resetNote, resetPassword, resetPassword2, resetTarget]);

  return (
    <div className="users-shell page-bleed">
      <div className="users-page-top">
        <div>
          <h1 className="users-title">{isOwner ? t('team.titleOwner') : t('team.titleBranch')}</h1>
          <p className="users-lede">
            {isOwner ? t('team.ledeOwner') : t('team.ledeBranch', { branch: branchName })}
          </p>
        </div>
        <div className="users-page-top-actions">
          <button type="button" className="users-btn-ghost" onClick={() => void refreshUsers()} disabled={usersLoading}>
            Refresh
          </button>
          {isOwner ? (
            <button type="button" className="users-btn-primary" onClick={() => onNavigate('users-add')}>
              Add user
            </button>
          ) : null}
        </div>
      </div>

      {isOwner ? (
      <div className="users-stats-row">
        <div className="users-stat users-stat--total">
          <span className="users-stat-label">Total accounts</span>
          <strong className="users-stat-value">{usersLoading ? '—' : users.length}</strong>
        </div>
        <div className="users-stat users-stat--owners">
          <span className="users-stat-label">Owners</span>
          <strong className="users-stat-value">
            {usersLoading ? '—' : users.filter((u) => normalizeRole(u.role) === 'owner').length}
          </strong>
        </div>
        <div className="users-stat users-stat--managers">
          <span className="users-stat-label">Managers</span>
          <strong className="users-stat-value">
            {usersLoading ? '—' : users.filter((u) => normalizeRole(u.role) === 'manager').length}
          </strong>
        </div>
        <div className="users-stat users-stat--staff">
          <span className="users-stat-label">Staff</span>
          <strong className="users-stat-value">
            {usersLoading ? '—' : users.filter((u) => normalizeRole(u.role) === 'employee').length}
          </strong>
        </div>
      </div>
      ) : null}

      {isOwner ? (
      <section className="users-reset-panel">
        <div className="users-reset-panel-head">
          <div>
            <h2 className="users-reset-title">Forgot password requests</h2>
            <p className="users-reset-lede">
              Staff can submit email on login screen. As owner, set a new password or dismiss.
            </p>
          </div>
          <button type="button" className="users-btn-ghost" onClick={() => void loadResetRequests()} disabled={resetLoading}>
            {resetLoading ? 'Refreshing…' : 'Refresh requests'}
          </button>
        </div>
        {resetError ? <div className="users-alert users-alert-error">{resetError}</div> : null}
        {pendingResetRequests.length === 0 ? (
          <p className="users-reset-empty">No pending password reset requests.</p>
        ) : (
          <ul className="users-reset-list">
            {pendingResetRequests.map((req) => (
              <li key={req.id} className="users-reset-item">
                <div className="users-reset-main">
                  <strong>{req.user?.name || req.email}</strong>
                  <span className="users-reset-email">{req.email}</span>
                  <span className="users-reset-time">
                    Requested: {req.requestedAt ? new Date(req.requestedAt).toLocaleString() : '—'}
                  </span>
                  {req.note ? <p className="users-reset-note">{req.note}</p> : null}
                </div>
                <div className="users-reset-actions">
                  <button type="button" className="users-btn-primary" onClick={() => openResetModal(req)}>
                    Set new password
                  </button>
                  <button
                    type="button"
                    className="users-btn-secondary"
                    disabled={String(resetActionId) === String(req.id)}
                    onClick={() => void dismissResetRequest(req)}
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      ) : null}

      <div className="users-table-card">
        {usersLoading ? (
          <div className="users-loading">
            <span className="users-loading-spinner" aria-hidden />
            <p>Loading users…</p>
          </div>
        ) : (
          <>
            {users.length > 0 && (
              <div className="users-table-toolbar">
                <label className="users-search-field">
                  <span className="users-visually-hidden">Search users</span>
                  <input
                    type="search"
                    className="users-search-input"
                    placeholder="Search name, email, or ID…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
                <label className="users-filter-field">
                  <span className="users-filter-label">Role</span>
                  <select className="users-filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                    <option value="all">All roles</option>
                    <option value="owner">Owners</option>
                    <option value="manager">Managers</option>
                    <option value="employee">Staff</option>
                  </select>
                </label>
              </div>
            )}
            <div className="users-table-scroll">
              <table className="users-data-table">
                <thead>
                  <tr>
                    <th className="users-col-id">#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    {isOwner ? <th>Branch</th> : null}
                    <th>Created</th>
                    {isOwner ? <th className="users-col-actions">Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={isOwner ? 7 : 5} className="users-table-empty">
                        <p className="users-empty-title">No team members yet</p>
                        <p className="users-empty-text">
                          {isOwner
                            ? 'Use Add user to create staff or owner accounts for a branch.'
                            : 'No coworkers listed for your branch yet.'}
                        </p>
                        {isOwner ? (
                        <button type="button" className="users-btn-primary users-btn-primary--inline" onClick={() => onNavigate('users-add')}>
                          Add first user
                        </button>
                        ) : null}
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={isOwner ? 7 : 5} className="users-table-empty users-table-empty--filter">
                        <p className="users-empty-title">No matching users</p>
                        <p className="users-empty-text">Try a different search or choose &quot;All roles&quot; in the filter.</p>
                        <button type="button" className="users-btn-secondary users-btn-primary--inline" onClick={() => { setSearchQuery(''); setRoleFilter('all'); }}>
                          Clear search &amp; filter
                        </button>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => {
                      const isSelf = user?.id != null && u.id === user.id;
                      return (
                        <tr key={u.id} className={isSelf ? 'users-row-self' : undefined}>
                          <td className="users-col-id users-mono">{u.id}</td>
                          <td>
                            <span className="users-name">{u.name}</span>
                            {isSelf && <span className="users-you-pill">You</span>}
                          </td>
                          <td className="users-email">{u.email || '—'}</td>
                          <td className="users-phone">{u.phone || '—'}</td>
                          <td>
                            <span className={roleBadgeClass(u.role)}>{roleLabelEn(u.role)}</span>
                          </td>
                          {isOwner ? <td>{u.branchName || '—'}</td> : null}
                          <td className="users-date">{u.createdAt ? new Date(u.createdAt).toLocaleString() : '—'}</td>
                          {isOwner ? (
                          <td className="users-col-actions">
                            <UserActionsCell
                              row={u}
                              currentUserId={user?.id}
                              removingId={removingId}
                              onView={setViewUser}
                              onEdit={setEditUser}
                              onRemove={onRemove}
                              onNavigate={onNavigate}
                              menuOpenId={menuOpenId}
                              setMenuOpenId={setMenuOpenId}
                            />
                          </td>
                          ) : null}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {viewUser && (
        <div className="users-modal-backdrop" role="presentation" onClick={() => setViewUser(null)}>
          <div className="users-modal" role="dialog" aria-labelledby="users-view-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="users-view-title" className="users-modal-title">
              User details
            </h2>
            <dl className="users-view-dl">
              <dt>Name</dt>
              <dd>{viewUser.name}</dd>
              <dt>Email</dt>
              <dd>{viewUser.email || '—'}</dd>
              <dt>Phone</dt>
              <dd>{viewUser.phone || '—'}</dd>
              <dt>Role</dt>
              <dd>{roleLabelEn(viewUser.role)}</dd>
              <dt>User ID</dt>
              <dd className="users-mono">{viewUser.id}</dd>
              <dt>Created</dt>
              <dd>{viewUser.createdAt ? new Date(viewUser.createdAt).toLocaleString() : '—'}</dd>
            </dl>
            <div className="users-modal-actions">
              <button
                type="button"
                className="users-btn-secondary"
                onClick={() => {
                  void navigator.clipboard?.writeText(viewUser.email).catch(() => {});
                }}
              >
                Copy email
              </button>
              <button type="button" className="users-btn-primary" onClick={() => setViewUser(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div className="users-modal-backdrop" role="presentation" onClick={() => !editSaving && setEditUser(null)}>
          <div className="users-modal users-modal--wide" role="dialog" aria-labelledby="users-edit-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="users-edit-title" className="users-modal-title">
              Edit user
            </h2>
            <p className="users-modal-sub">
              Update name, email, role, and branch. Staff and managers stay on their branch; owners get full access.
              Leave both password fields empty to keep the current password.
            </p>
            <form className="users-modal-form" onSubmit={submitEdit}>
              {isOwner ? (
                <>
                  <label className="users-field">
                    <span className="users-field-label">Role</span>
                    <select value={editRole} onChange={(e) => setEditRole(e.target.value)} disabled={editSaving}>
                      <option value="employee">Staff</option>
                      <option value="manager">Manager</option>
                      <option value="owner">Owner</option>
                    </select>
                  </label>
                  <label className="users-field">
                    <span className="users-field-label">Branch</span>
                    <select
                      value={editBranchId}
                      onChange={(e) => setEditBranchId(e.target.value)}
                      disabled={editSaving || !branches.length}
                    >
                      {!branches.length ? (
                        <option value="">No branches loaded</option>
                      ) : (
                        branches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                </>
              ) : null}
              <label className="users-field">
                <span className="users-field-label">Full name</span>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} required disabled={editSaving} />
              </label>
              <label className="users-field">
                <span className="users-field-label">Phone</span>
                <input
                  type="tel"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  placeholder="0712 345 678"
                  disabled={editSaving}
                />
              </label>
              <label className="users-field">
                <span className="users-field-label">Email</span>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  required={editRole === 'owner'}
                  disabled={editSaving}
                />
              </label>
              <label className="users-field">
                <span className="users-field-label">New password (optional)</span>
                <input
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Leave empty to keep current password"
                  disabled={editSaving}
                />
              </label>
              <label className="users-field">
                <span className="users-field-label">Confirm new password</span>
                <input
                  type="password"
                  value={editPassword2}
                  onChange={(e) => setEditPassword2(e.target.value)}
                  autoComplete="new-password"
                  disabled={editSaving}
                />
              </label>
              {editError && <div className="users-alert users-alert-error">{editError}</div>}
              <div className="users-modal-actions">
                <button type="button" className="users-btn-secondary" disabled={editSaving} onClick={() => setEditUser(null)}>
                  Cancel
                </button>
                <button type="submit" className="users-btn-primary" disabled={editSaving}>
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="users-modal-backdrop" role="presentation" onClick={() => setResetTarget(null)}>
          <div className="users-modal users-modal--wide" role="dialog" aria-labelledby="users-reset-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="users-reset-title" className="users-modal-title">
              Reset password
            </h2>
            <p className="users-modal-sub">
              Account: <strong>{resetTarget.user?.name || 'User'}</strong> ({resetTarget.email})
            </p>
            <form className="users-modal-form" onSubmit={submitPasswordReset}>
              <label className="users-field">
                <span className="users-field-label">New password</span>
                <input
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
              <label className="users-field">
                <span className="users-field-label">Confirm new password</span>
                <input
                  type="password"
                  value={resetPassword2}
                  onChange={(e) => setResetPassword2(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
              <label className="users-field">
                <span className="users-field-label">Owner note (optional)</span>
                <input
                  type="text"
                  value={resetNote}
                  onChange={(e) => setResetNote(e.target.value)}
                  maxLength={800}
                />
              </label>
              {resetError ? <div className="users-alert users-alert-error">{resetError}</div> : null}
              <div className="users-modal-actions">
                <button type="button" className="users-btn-secondary" disabled={String(resetActionId) === String(resetTarget.id)} onClick={() => setResetTarget(null)}>
                  Cancel
                </button>
                <button type="submit" className="users-btn-primary" disabled={String(resetActionId) === String(resetTarget.id)}>
                  {String(resetActionId) === String(resetTarget.id) ? 'Saving…' : 'Reset password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <p className="users-page-foot">
        <button type="button" className="users-btn-link" onClick={() => onNavigate('overview')}>
          ← Back to dashboard
        </button>
      </p>
    </div>
  );
};

export default UsersListPage;
